// 「顧客から見た店舗」の API (#1138): 店舗閲覧/卓QR/店舗セッション/レビュー/
// メニュー閲覧。店舗オーナー側の運営操作は storeOwner.ts へ。配置ルールは index.ts 参照。
import { ApiError, apiFetch } from './_client'
import type { VariationGroup, VariationSnapshot } from '../../lib/menuVariation'
import type { TimeChargeState } from '../../store/useStore'

/** #2355 いま飛んでいる `/manage` の読み取り (店ごとに 1 本)。詳細は getManagedStore */
const managedInFlight = new Map<string, Promise<unknown>>()

// #8 おすすめ店舗: 実利用データ (決済/口コミ/選好ログ/保存プラン) 由来の推薦。
// reason は plan カタログ (#1253) と同じ「最も強かった成分 1 つ」(説明可能性)。
// #2036 卓の滞在時間の状態。サーバが着席時に焼き込んだ時刻から計算して返す
// (店が後から設定を変えても、座っている客の条件は変わらない)。
export interface TableStayState {
  staysUntilAt: string
  /** 店が案内を「出さない」にしていると null (ラストオーダーの行は出さない) */
  lastOrderAt: string | null
  remainingMinutes: number
  lastOrderPassed: boolean
  stayOver: boolean
  stopOrdersAtStayLimit: boolean
}

/**
 * 店のボードに出す「使用中の卓」1 行。
 *
 * 滞在時間の項目が **任意** なのは、#2036 より前から座っている卓には時刻が
 * 焼き込まれていないため (サーバは席の情報だけを返す)。必ずあるつもりで読むと
 * 「残り NaN 分」になるので、`staysUntilAt` の有無で分岐する。
 */
export interface TableSessionRow extends Partial<TableStayState> {
  tableNumber: string
  partySize?: number
  startedAt?: string | null
  // #2039 バインダーの呼び名・種別と、付いているプランの状態 (現在の時間料金)。
  // 注文が 1 つも無いバインダー (全員無料など) も、これで一覧に出せる。
  label?: string | null
  binderType?: string | null
  allYouCan?: import('../../store/useStore').AllYouCanState | null
  // #2155 この卓で受け取る自動のお通し・席料 (外した分・受け取り済みを除いたサーバの値)
  autoExtras?: { name: string; price: number; quantity: number }[]
}

export type StoreRecoReason = 'your_taste' | 'compatriot' | 'same_age' | 'popular'
export interface StoreRecoEntry {
  score: number
  reason: StoreRecoReason | null
}

export const storesApi = {
  // Stores
  getStores: async (category?: string) => {
    const path = category ? `/stores/nearby?category=${category}` : '/stores/nearby'
    const res = await apiFetch(path)
    return res.json()
  },

  // #8 おすすめ店舗スコア。未集計なら空 (呼び側は従来の並びへフォールバック)。
  // ログイン時はトークン自動添付で国籍/年代/ペルソナ個人化がサーバ側で効く。
  getStoreRecommendations: async () => {
    const res = await apiFetch('/stores/recommendations')
    return res.json() as Promise<{ scores?: Record<string, StoreRecoEntry> }>
  },

  // お客様向けの公開の店の情報。**お店側の画面からは呼ばない** (#2199: お客様に見せる
  // 項目だけに絞る。お店側は下の getManagedStore)
  getStore: async (storeId: string) => {
    const res = await apiFetch(`/stores/${storeId}`)
    return res.json()
  },

  /**
   * #2199 お店側の画面が読む店の情報 (doc の項目を全部)。owner|manager|staff。
   * apiFetch がオーナー or スタッフのトークンを自動添付。権限が無ければ 403 (ApiError)
   *
   * #2355 **同時に走っている同じ読み取りは 1 本にまとめる。**
   *
   * 店舗画面には枠 (StaffTabBar / StoreSidebar) が常に載っていて、そこと各ページが
   * それぞれ同じ店を読む。どれも開いた瞬間に走るので、**1 画面で同じ読み取りが 3 本**
   * 出ていた (体験環境で実測)。この口は店 1 + メニュー全件 + レビュー 50 を読むので
   * (`_role_and_store` のコメント: 実測 42 reads)、3 本で 126 reads になる。
   *
   * **まとめるのは「いま飛んでいる最中」のものだけで、結果は持ち越さない。**
   * 時間で持つと保存した直後に古い値を返しかねないが、飛んでいる最中に合流するだけなら
   * 「同じ瞬間に聞いた人は同じ答えを見る」でしかない。
   *
   * ただし**保存の直後の読み直しは合流させない** (`fresh`)。保存より前に始まっていた
   * 読み取りに合流すると、保存前の値が返る。`fresh` は飛んでいる最中のものも捨てるので、
   * 後から来た人が古い答えを掴むこともない。
   */
  getManagedStore: async (storeId: string, opts?: { fresh?: boolean }) => {
    if (opts?.fresh) managedInFlight.delete(storeId)
    else {
      const running = managedInFlight.get(storeId)
      if (running) return running
    }
    const pending = (async () => {
      const res = await apiFetch(`/stores/${encodeURIComponent(storeId)}/manage`)
      return res.json()
    })()
    if (!opts?.fresh) {
      managedInFlight.set(storeId, pending)
      // 成否にかかわらず忘れる (失敗を覚えて再試行を塞がない)。自分が置いたものだけ消す
      void pending.then(() => {}, () => {}).then(() => {
        if (managedInFlight.get(storeId) === pending) managedInFlight.delete(storeId)
      })
    }
    return pending
  },

  // #949 (管理番号_011) opaque table QR tokens.
  // Owner: get the per-table token map to build `?t={token}` QRs.
  // #994 owner|manager。apiFetch がオーナー or スタッフ(店長)トークンを自動添付。
  getTableQrTokens: async (storeId: string) => {
    const res = await apiFetch(`/stores/${storeId}/table-qr-tokens`)
    return res.json() as Promise<{ tokens?: Record<string, string>; detail?: string }>
  },
  // Customer: resolve a scanned table token to its table number.
  // #968 active な卓セッションがあれば session を返す（ウェルカム/人数をスキップ判定）。
  resolveTable: async (storeId: string, token: string) => {
    const res = await apiFetch(`/stores/${storeId}/resolve-table?t=${encodeURIComponent(token)}`)
    return res.json() as Promise<{
      tableNumber?: number
      session?: { partySize?: number } | null
      detail?: string
    }>
  },

  // #968 Customer (公開・ログイン不要): ウェルカム→人数確認で人数を確定し卓セッションを
  // 作成（着席）。get-or-create で別端末の同卓2人目は既存を返す。
  createTableSession: async (storeId: string, token: string, partySize: number) => {
    const res = await apiFetch(`/stores/${storeId}/table-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ t: token, partySize }),
    })
    return res.json() as Promise<{ tableNumber?: number; partySize?: number; detail?: string }>
  },

  // #2036 卓の「ご利用時間 / ラストオーダー」。卓 QR の token は URL から消えるので、
  // 着席の応答だけでなく卓番号でも読めるようにしてある (残り時間は減っていくため)。
  getTableStay: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch(`/stores/${storeId}/table-stay/${encodeURIComponent(tableNumber)}`)
    return res.json() as Promise<{ tableNumber: string; stay: TableStayState | null }>
  },

  // #2039 バインダーを開ける (店員)。番号の種別に付いているプランが同時に付き、
  // **この瞬間が時間料金の起点**。使用中の番号は 409 `binder_in_use`。
  // #2039 Customer (公開・ログイン不要): バインダーの QR から自分の伝票を読む。
  // 認可はトークンだけ。開いていない伝票は `{open: false}` だけが返る。
  getBinderBill: async (storeId: string, token: string) => {
    const res = await apiFetch(`/binders/${storeId}/bill?t=${encodeURIComponent(token)}`)
    return res.json() as Promise<BinderBillState>
  },

  openBinder: async (
    storeId: string,
    payload: { binderNumber: string; partySize?: number; freePartySize?: number; label?: string },
  ) => {
    const res = await apiFetch(`/binders/${storeId}/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return res.json() as Promise<{
      binderNumber: string
      partySize: number
      label: string | null
      binderType: string | null
      allYouCan: import('../../store/useStore').AllYouCanState | null
    }>
  },

  // 店: 使用中の卓と滞在時間の状態 (注文管理のボードで使う)
  listTableSessions: async (storeId: string) => {
    const res = await apiFetch(`/stores/${storeId}/table-sessions`)
    return res.json() as Promise<{ sessions: TableSessionRow[] }>
  },

  // Reviews
  submitReview: async (storeId: string, rating: number, tags: string[], comment: string) => {
    const res = await apiFetch('/reviews', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store_id: storeId, rating, tags, comment }),
    })
    return res.json()
  },

  // Share logs (#419 / #69 二段構成: x_repost = 公式ポストのリポスト、
  // instagram_story = 公式 IG 投稿のストーリーズ紹介)
  submitShareLog: async (
    storeId: string,
    platform:
      | 'x' | 'facebook' | 'line' | 'whatsapp' | 'instagram'
      | 'x_repost' | 'instagram_story' | 'copy_link' | 'other',
    source: 'payment_complete' | 'store_detail' | 'review',
  ) => {
    const res = await apiFetch('/share-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store_id: storeId, platform, source }),
    })
    return res.json()
  },

  // Store Management
  updateStore: async (storeId: string, updates: Record<string, unknown>) => {
    const res = await apiFetch(`/stores/${storeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    return res.json()
  },

  // #1910 ログイン中のアカウントが、この店を店舗管理の画面で扱ってよいか。
  // 権限が無ければ 403、店が無ければ 404 (apiFetch が ApiError を投げる)。
  getStoreAccess: async (storeId: string) => {
    const res = await apiFetch(`/stores/${encodeURIComponent(storeId)}/access`)
    return res.json() as Promise<{ storeId: string; role: 'owner' | 'manager' | 'staff' }>
  },

  // #1439 スタッフに見せる範囲。**オーナー専用の別エンドポイント** — updateStore
  // (owner|manager) に相乗りさせると店長が自分の権限を書き換えられてしまう。
  updateStoreRolePolicy: async (storeId: string, rolePolicy: Record<string, Record<string, boolean>>) => {
    const res = await apiFetch(`/stores/${storeId}/role-policy`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rolePolicy }),
    })
    return res.json()
  },

  addMenuItem: async (storeId: string, item: { name: string; nameEn: string; price: number; imageUrl?: string; description?: string; category?: string; allergens?: string[]; dietary?: string[] }) => {
    const res = await apiFetch(`/stores/${storeId}/menu`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    })
    return res.json()
  },

  updateMenuItem: async (storeId: string, itemId: string, updates: Record<string, unknown>) => {
    const res = await apiFetch(`/stores/${storeId}/menu/${itemId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    return res.json()
  },

  deleteMenuItem: async (storeId: string, itemId: string) => {
    const res = await apiFetch(`/stores/${storeId}/menu/${itemId}`, {
      method: 'DELETE',
    })
    return res.json()
  },

  // #994 売り切れトグル: menu 編集本体(owner|manager)と分離した全ロール可の専用経路。
  // #2031 選択肢 (味玉・たれ) の売り切れ。**定義の保存 (updateStore) に相乗りさせない** —
  // あちらは配列まるごとの書き込みで、画面が古いと他の端末が付けた売り切れを黙って戻す。
  // サーバが今の値を読んで 1 つだけ変え、変えた後の定義を返す。
  setVariationOptionSoldOut: async (
    storeId: string, groupId: string, optionId: string, soldOut: boolean,
  ) => {
    const res = await apiFetch(`/stores/${storeId}/variation-options/sold-out`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupId, optionId, soldOut }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update option sold-out')
    return res.json() as Promise<{ variationGroups: VariationGroup[] }>
  },

  setMenuItemSoldOut: async (storeId: string, itemId: string, soldOut: boolean) => {
    const res = await apiFetch(`/stores/${storeId}/menu/${itemId}/sold-out`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ soldOut }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update sold-out')
    return res.json()
  },
}

/** #2039 お客様が見る自分の伝票 (`GET /binders/{id}/bill`)。金額の正はサーバ。 */
export interface BinderBillState {
  binderNumber: string
  storeName: string
  storeNameEn?: string
  /** 店が客のスマホからの注文を受けているか */
  customerOrder: boolean
  /** 使用中か。false のときは下の項目は返らない (前の客の内容を出さない) */
  open: boolean
  /** 支払いが済んで、店員がまだ閉じていない */
  paid?: boolean
  partySize?: number | null
  timeCharge?: TimeChargeState | null
  orders?: {
    id: string
    totalAmount: number
    items: {
      name: string
      nameEn?: string
      nameLocalized?: string | null
      /** nameLocalized が何語か */
      locale?: string | null
      quantity: number
      price: number
      variations?: VariationSnapshot[] | null
      timeCharge?: boolean
      unitPriceYen?: number | null
      unitCount?: number | null
    }[]
  }[]
  ordersTotal?: number
  /** お通し・チャージ (店の設定で自動に付く分) */
  extras?: { name: string; price: number; quantity: number }[]
  extrasTotal?: number
  totalAmount?: number
  checkoutRequested?: boolean
}
